"use client";

import { useMemo, useState } from "react";
import { useTransactions, useTransactionsKPI, downloadInvoice } from "core";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "components/ui/table";
import Pagination from "components/ui/pagination";
import { toast } from "components/ui/toast";
import { Info, Download, FileText, X } from "lucide-react";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "components/ui/tooltip";
import PageContainer from "components/ui/page-container";
import { Skeleton } from "components/ui/skeleton";
import { DatePicker } from "components/ui/date-picker";
import { StatusBadge as SharedStatusBadge } from "components/ui/status-badge";
import { EmptyState } from "components/ui/empty-state";
import { ErrorState } from "components/ui/error-state";
import TransactionsHeader from "./TransactionsHeader";

interface TransactionsPageProps {

  embedded?: boolean;
}

export default function TransactionsPage({ embedded = false }: TransactionsPageProps) {
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useTransactions(page);
  const { data: kpiData, isLoading: kpiLoading } = useTransactionsKPI();

  const filterActive = Boolean(startDate && endDate);
  const rows = useMemo(() => {
    const all = data?.data ?? [];
    if (!filterActive) return all;
    const from = new Date(startDate);
    const to = new Date(endDate);
    if (from > to) return all;
    return all.filter((t: any) => {
      const d = new Date(t.paymentDate ?? t.date ?? t.createdAt);
      return d >= from && d <= to;
    });
  }, [data, startDate, endDate, filterActive]);

  const clearFilter = () => { setStartDate(""); setEndDate(""); };

  const handleDownload = async (t: any) => {
    try {
      const url = await downloadInvoice(t.transactionId);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${t.transactionId}.pdf`;
      a.click();
      toast.success("Invoice downloaded successfully");
    } catch {
      toast.error("Something went wrong while downloading invoice");
    }
  };

  const formatDate = (str: string) => {
    const d = new Date(str);
    const date = d.toLocaleDateString("en", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${date} · ${time}`;
  };

  /*
   * Only the first load swaps the table for a skeleton. Changing page is a
   * background refetch, and blanking the whole table for it made pagination
   * flash — the slim progress bar at the top of the card already reports it.
   */
  const showSkeleton = isLoading;

  /*
   * Was a local three-branch badge with its own icons and hardcoded palette.
   * The shared StatusBadge already understands `succeeded` / `paid` / `failed`
   * / `pending`, so transaction states now match every other status pill.
   */
  const StatusBadge = ({ status }: { status: string }) => (
    <SharedStatusBadge status={status} size="sm" label={status} />
  );

  /*
   * A failed request used to fall through to "No transactions found", which
   * told the user their history was empty when the server had simply not
   * answered. Failure and emptiness are now distinct, and failure offers a
   * retry.
   */
  const emptyOrError = isError ? (
    <ErrorState
      bare
      title="Couldn't load your transactions"
      description="The request didn't complete. Your payment history is safe — this is only the view."
      error={error}
      onRetry={() => refetch()}
    />
  ) : (
    <EmptyState
      bare
      size="sm"
      icon={FileText}
      variant={filterActive ? "no-results" : "empty"}
      title={filterActive ? "No transactions in that date range" : "No transactions yet"}
      description={
        filterActive
          ? "Nothing on this page falls between those dates. Clear the filter, or try another page."
          : "Invoices and payments will appear here once your first charge goes through."
      }
      primaryAction={filterActive ? { label: "Clear filter", onClick: clearFilter } : null}
    />
  );

  const body = (
    <>
      <TransactionsHeader
        totalTransactions={kpiData?.totalCount ?? 0}
        totalAmount={kpiData?.totalAmount ?? 0}
        successRate={kpiData?.successRate ?? 0}
        totalCredits={kpiData?.totalCredits ?? 0}
        isLoading={kpiLoading}
        embedded={embedded}
      />

      <div className="relative bg-surface dark:bg-dark-800 rounded-2xl shadow-sm
        border border-line dark:border-white/[0.07] overflow-hidden">

        {/* Gradient accent bar */}
        <div className="h-[2px] w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />

        {/* Card header */}
        <div className="flex items-center justify-between px-5 py-4
          border-b border-line-subtle dark:border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl
              bg-blue-50 dark:bg-blue-500/10
              border border-blue-100 dark:border-blue-500/20
              flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-title-sm font-bold text-content dark:text-white leading-tight">
                All Transactions
              </h2>
              {!isLoading && (
                <p className="text-micro text-content-muted dark:text-gray-400 font-medium tabular-nums">
                  {filterActive
                    ? `${rows.length} of ${data?.data?.length ?? 0} on this page`
                    : `${data?.total ?? 0} records`}
                </p>
              )}
            </div>
          </div>

          {/* Filter this page by date. Scoped label — see the note on `rows`. */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span className="hidden xl:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl
              bg-gray-50 dark:bg-white/[0.04]
              border border-line dark:border-white/[0.07]
              text-micro font-semibold text-content-subtle dark:text-gray-500">
              <Download className="w-3 h-3" />
              Click a Transaction ID to download its invoice
            </span>

            {/* Shared DatePicker rather than `<input type="date">`. The value
                contract is identical — both sides still exchange `yyyy-MM-dd` —
                so the filtering logic below is untouched. */}
            <div className="inline-flex items-center gap-1.5">
              <DatePicker
                id="tx-from"
                aria-label="Filter this page from date"
                placeholder="From"
                max={endDate || today}
                value={startDate}
                onChange={setStartDate}
                className="h-9 w-[9.5rem] text-meta"
              />
              <span className="text-content-subtle text-meta flex-shrink-0" aria-hidden="true">→</span>
              <DatePicker
                id="tx-to"
                aria-label="Filter this page to date"
                placeholder="To"
                min={startDate || undefined}
                max={today}
                value={endDate}
                onChange={setEndDate}
                className="h-9 w-[9.5rem] text-meta"
              />
              {(startDate || endDate) && (
                <button
                  type="button"
                  onClick={clearFilter}
                  aria-label="Clear date filter"
                  className="p-1 rounded-lg text-content-subtle hover:text-content hover:bg-surface-hover transition-colors focus-ring"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── DESKTOP TABLE (md+) ── */}
        <div className="hidden lg:block overflow-x-auto">
          <Table>
            <TableHeader>
              <tr>
                <TableHead className="w-14 pl-5">#</TableHead>
                <TableHead>Transaction ID</TableHead>
                <TableHead>Date &amp; Time</TableHead>
                <TableHead className="text-center">Credits Added</TableHead>
                <TableHead className="text-center">Amount</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead>Type</TableHead>
              </tr>
            </TableHeader>

            <TableBody>
              {showSkeleton ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    <TableCell className="pl-5">
                      <Skeleton variant="text" className="h-4 w-8" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" className="h-4 w-44" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" className="h-4 w-36" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton variant="text" className="h-4 w-16 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton variant="text" className="h-4 w-16 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton variant="chip" className="h-6 w-20 mx-auto rounded-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" className="h-4 w-24" />
                    </TableCell>
                  </TableRow>
                ))
              ) : rows.length ? (
                rows.map((t: any, i: number) => (
                  <TableRow key={t.id}>
                    <TableCell className="pl-5 text-content-subtle dark:text-gray-500 font-medium text-[13px]">
                      {(page - 1) * 10 + i + 1}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleDownload(t)}
                        className="inline-flex items-center gap-1.5
                          text-[13px] font-semibold text-blue-600 dark:text-blue-400
                          hover:text-blue-800 dark:hover:text-blue-300
                          hover:underline underline-offset-2
                          transition-colors duration-150"
                      >
                        <Download className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate max-w-[200px]">{t.transactionId}</span>
                      </button>
                    </TableCell>
                    <TableCell className="text-ui text-content-secondary dark:text-gray-300 whitespace-nowrap">
                      {formatDate(t.paymentDate)}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-[13px] font-semibold text-indigo-600 dark:text-indigo-400">
                        +{t.credits?.toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center gap-1
                        text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                        ${t.amount.toFixed(2)}
                        {t.cStatus === "TERMINATED" && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 cursor-pointer flex-shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p className="text-xs font-medium">
                                  Campaign Status: {t.cStatus}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <StatusBadge status={t.status} />
                    </TableCell>
                    <TableCell>
                      <span className="text-[12px] font-semibold px-2 py-1 rounded-lg
                        bg-gray-100 dark:bg-dark-700
                        text-content-secondary dark:text-gray-300
                        border border-line dark:border-dark-600">
                        {t.paymentType}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="p-0">
                    {emptyOrError}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* ── MOBILE CARD LIST (< md) ── */}
        <div className="lg:hidden divide-y divide-gray-100 dark:divide-dark-700">
          {showSkeleton ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={`m-skeleton-${i}`} className="px-4 py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Skeleton variant="text" className="h-4 w-40" />
                  <Skeleton variant="chip" className="h-6 w-20 rounded-full" />
                </div>
                <div className="flex items-center justify-between">
                  <Skeleton variant="text" className="h-3.5 w-32" />
                  <Skeleton variant="text" className="h-3.5 w-16" />
                </div>
                <div className="flex items-center justify-between">
                  <Skeleton variant="text" className="h-3.5 w-24" />
                  <Skeleton variant="text" className="h-3.5 w-20" />
                </div>
              </div>
            ))
          ) : rows.length ? (
            rows.map((t: any, i: number) => (
              <div key={t.id} className="px-4 py-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[11px] font-semibold text-content-subtle dark:text-gray-500 flex-shrink-0">
                      #{(page - 1) * 10 + i + 1}
                    </span>
                    <button
                      onClick={() => handleDownload(t)}
                      className="inline-flex items-center gap-1
                        text-[13px] font-semibold text-blue-600 dark:text-blue-400
                        hover:text-blue-800 dark:hover:text-blue-300
                        transition-colors duration-150 min-w-0"
                    >
                      <Download className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate max-w-[160px]">{t.transactionId}</span>
                    </button>
                  </div>
                  <StatusBadge status={t.status} />
                </div>

                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-content-muted dark:text-gray-400">
                    {formatDate(t.paymentDate)}
                  </span>
                  <span className="inline-flex items-center gap-1 font-bold text-content dark:text-gray-100">
                    ${t.amount.toFixed(2)}
                    {t.cStatus === "TERMINATED" && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 cursor-pointer flex-shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p className="text-xs font-medium">Campaign Status: {t.cStatus}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-indigo-600 dark:text-indigo-400">
                    +{t.credits?.toLocaleString()} credits
                  </span>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md
                    bg-gray-100 dark:bg-dark-700
                    text-content-secondary dark:text-gray-300
                    border border-line dark:border-dark-600">
                    {t.paymentType}
                  </span>
                </div>
              </div>
            ))
          ) : (
            emptyOrError
          )}
        </div>

        {/* Slim loading bar during background page-change refetch */}
        {isFetching && !isLoading && (
          <div className="absolute top-0 left-0 right-0 h-[2px] z-10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 via-indigo-400 to-violet-500
              animate-[shimmer_1.4s_ease-in-out_infinite]"
              style={{ backgroundSize: "200% 100%", animation: "shimmer 1.4s ease-in-out infinite" }}
            />
          </div>
        )}
      </div>

      <div className="mt-5">
        <Pagination
          totalEntries={data?.total ?? 0}
          entriesPerPage={10}
          currentPage={page}
          onPageChange={setPage}
          showSummary
        />
      </div>
    </>
  );

  return embedded ? (
    <div className="flex flex-col gap-5">{body}</div>
  ) : (
    <PageContainer>{body}</PageContainer>
  );
}
